import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Brand {
  name: string;
}

interface OperationMode {
  delivery: boolean;
  takeaway: boolean;
}

interface OperationHours {
  start: string;
  end: string;
}

interface Store {
  address: string;
  alias: string;
  brands: string;
  contact: string;
  operationMode: OperationMode;
  operationHours: OperationHours;
}

interface BusinessState {
  data: {
    businessName: string;
    businessLocation: string;
    contact: string;
    brands: Brand[];
    stores: Record<string, Store>;
  };
  status: {
    isComplete: boolean;
    hasError: boolean;
  };
  errors: Record<string, string>;
}

const initialState: BusinessState = {
  data: {
    businessName: '',
    businessLocation: '',
    contact: '',
    brands: [],
    stores: {},
  },
  status: {
    isComplete: false,
    hasError: false,
  },
  errors: {},
};

const businessSlice = createSlice({
  name: 'business',
  initialState,
  reducers: {
    updateBusinessDetails: (
      state,
      action: PayloadAction<Partial<BusinessState['data']>>
    ) => {
      state.data = {
        ...state.data,
        ...action.payload,
      };
    },
    addBrand: (state, action: PayloadAction<string>) => {
      state.data.brands.push({ name: action.payload });
    },
    removeBrand: (state, action: PayloadAction<string>) => {
      state.data.brands = state.data.brands.filter(
        brand => brand.name !== action.payload
      );
    },
    setBusinessStatus: (
      state,
      action: PayloadAction<BusinessState['status']>
    ) => {
      state.status = action.payload;
    },
    setBusinessErrors: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.errors = action.payload;
    },
    clearBusinessErrors: state => {
      state.errors = {};
    },
    addStore: (
      state,
      action: PayloadAction<{ storeName: string; storeData: Store }>
    ) => {
      state.data.stores[action.payload.storeName] = action.payload.storeData;
    },
    updateStore: (
      state,
      action: PayloadAction<{ storeName: string; storeData: Partial<Store> }>
    ) => {
      if (state.data.stores[action.payload.storeName]) {
        state.data.stores[action.payload.storeName] = {
          ...state.data.stores[action.payload.storeName],
          ...action.payload.storeData,
        };
      }
    },
    removeStore: (state, action: PayloadAction<string>) => {
      delete state.data.stores[action.payload];
    },
  },
});

export const {
  updateBusinessDetails,
  addBrand,
  removeBrand,
  setBusinessStatus,
  setBusinessErrors,
  clearBusinessErrors,
  addStore,
  updateStore,
  removeStore,
} = businessSlice.actions;

export default businessSlice.reducer;
